/**
 * Form creazione nuovo ordine di acquisto.
 * Selezione fornitore, rappresentante, righe con autocomplete articoli.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Combobox, type ComboboxOption, Input, Label } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';

interface Fornitore {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  partita_iva: string | null;
}

interface Rappresentante {
  id: string;
  nome: string;
  cognome: string;
  email: string | null;
}

interface Articolo {
  id: string;
  sku_interno: string;
  descrizione: string;
}

interface CatalogoVoce {
  id: string;
  sku_fornitore: string;
  descrizione: string;
  prezzo_unitario: string;
  unita_misura: string;
}

interface RigaBozza {
  key: number;
  articolo_id: string;
  codice_articolo: string;
  descrizione: string;
  unita_misura: string;
  quantita_ordinata: string;
  prezzo_unitario: string;
}

export function NuovoOrdine() {
  const navigate = useNavigate();
  const { currency } = useFormatters();
  const token = localStorage.getItem('token');

  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [fornitoreId, setFornitoreId] = useState('');
  const [fornitoreSearch, setFornitoreSearch] = useState('');
  const [rappresentanti, setRappresentanti] = useState<Rappresentante[]>([]);
  const [rappresentanteId, setRappresentanteId] = useState('');
  const [dataConsegna, setDataConsegna] = useState('');
  const [note, setNote] = useState('');
  const [righe, setRighe] = useState<RigaBozza[]>([]);
  const [saving, setSaving] = useState(false);
  const [nextKey, setNextKey] = useState(1);

  // Ricerca articoli
  const [articoliSearch, setArticoliSearch] = useState('');
  const [articoli, setArticoli] = useState<Articolo[]>([]);

  // Catalogo fornitore
  const [catalogo, setCatalogo] = useState<CatalogoVoce[]>([]);

  // Carica fornitori
  useEffect(() => {
    fetch('/api/v1/fornitori', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then(setFornitori)
      .catch(() => {});
  }, [token]);

  // Carica rappresentanti del fornitore selezionato
  useEffect(() => {
    if (!fornitoreId) {
      setRappresentanti([]);
      return;
    }
    fetch(`/api/v1/fornitori/${fornitoreId}/rappresentanti`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setRappresentanti)
      .catch(() => setRappresentanti([]));

    // Carica catalogo fornitore
    fetch(`/api/v1/fornitori/${fornitoreId}/catalogo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setCatalogo)
      .catch(() => setCatalogo([]));
  }, [fornitoreId, token]);

  // Ricerca articoli magazzino
  useEffect(() => {
    if (articoliSearch.length < 2) {
      setArticoli([]);
      return;
    }
    fetch(`/api/v1/magazzino/articoli?search=${encodeURIComponent(articoliSearch)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setArticoli)
      .catch(() => setArticoli([]));
  }, [articoliSearch, token]);

  const aggiungiRigaDaArticolo = (art: Articolo) => {
    // Cerca prezzo nel catalogo fornitore
    const voceCatalogo = catalogo.find(
      (c: any) => c.articolo_magazzino_id === art.id || c.descrizione === art.descrizione
    );

    const nuovaRiga: RigaBozza = {
      key: nextKey,
      articolo_id: art.id,
      codice_articolo: art.sku_interno,
      descrizione: art.descrizione,
      unita_misura: voceCatalogo?.unita_misura || 'pz',
      quantita_ordinata: '1',
      prezzo_unitario: voceCatalogo?.prezzo_unitario || '0',
    };
    setRighe((prev) => [...prev, nuovaRiga]);
    setNextKey((k) => k + 1);
    setArticoliSearch('');
    setArticoli([]);
  };

  const rimuoviRiga = (key: number) => {
    setRighe((prev) => prev.filter((r) => r.key !== key));
  };

  const aggiornaRiga = (key: number, field: keyof RigaBozza, value: string) => {
    setRighe((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
    );
  };

  const totale = righe.reduce((acc, r) => {
    return acc + Number(r.quantita_ordinata || 0) * Number(r.prezzo_unitario || 0);
  }, 0);

  const handleSalva = async () => {
    if (!fornitoreId) return;
    setSaving(true);

    try {
      // 1. Crea ordine
      const ordineRes = await fetch('/api/v1/ordini', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fornitore_id: fornitoreId,
          rappresentante_id: rappresentanteId || null,
          data_consegna_attesa: dataConsegna || null,
          note: note || null,
        }),
      });

      if (!ordineRes.ok) {
        setSaving(false);
        return;
      }

      const ordine = await ordineRes.json();

      // 2. Aggiungi righe
      for (const riga of righe) {
        await fetch(`/api/v1/ordini/${ordine.id}/righe`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            articolo_id: riga.articolo_id,
            codice_articolo: riga.codice_articolo,
            descrizione: riga.descrizione,
            unita_misura: riga.unita_misura,
            quantita_ordinata: Number(riga.quantita_ordinata),
            prezzo_unitario: Number(riga.prezzo_unitario),
          }),
        });
      }

      navigate(`/ordini/${ordine.id}`);
    } catch {
      setSaving(false);
    }
  };

  const nomeFornitore = (f: Fornitore) =>
    f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || '—';

  return (
    <div className="p-6 space-y-6 w-full">
      <h1 className="text-xl font-outfit font-semibold text-foreground">
        Nuovo ordine di acquisto
      </h1>

      {/* Testata */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Fornitore *</Label>
            <Combobox
              value={(() => {
                const sel = fornitori.find((f) => f.id === fornitoreId);
                return sel ? nomeFornitore(sel) : fornitoreSearch;
              })()}
              onInputChange={(v) => {
                setFornitoreSearch(v);
                if (fornitoreId) {
                  const sel = fornitori.find((f) => f.id === fornitoreId);
                  if (sel && v !== nomeFornitore(sel)) {
                    setFornitoreId('');
                    setRappresentanteId('');
                  }
                }
              }}
              onSelect={(opt) => {
                setFornitoreId(opt.value);
                setFornitoreSearch('');
                setRappresentanteId('');
              }}
              options={fornitori
                .filter((f) => {
                  if (!fornitoreSearch) return true;
                  const q = fornitoreSearch.toLowerCase();
                  return (
                    nomeFornitore(f).toLowerCase().includes(q) ||
                    (f.partita_iva || '').toLowerCase().includes(q)
                  );
                })
                .map<ComboboxOption>((f) => ({
                  value: f.id,
                  label: nomeFornitore(f),
                  sublabel: f.partita_iva ? `P.IVA ${f.partita_iva}` : '',
                }))}
              placeholder="Cerca fornitore per nome o P.IVA..."
            />
          </div>

          <div>
            <Label>Rappresentante</Label>
            <select
              value={rappresentanteId}
              onChange={(e) => setRappresentanteId(e.target.value)}
              className="w-full rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
              disabled={!fornitoreId}
            >
              <option value="">Nessuno</option>
              {rappresentanti.map((r: any) => (
                <option key={r.id} value={r.id}>
                  {r.nome} {r.cognome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Data consegna attesa</Label>
            <Input
              type="date"
              value={dataConsegna}
              onChange={(e: any) => setDataConsegna(e.target.value)}
            />
          </div>
          <div>
            <Label>Note</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full rounded border border-border bg-card text-foreground px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>
      </Card>

      {/* Righe */}
      <Card className="p-4 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">Righe ordine</h2>

        {/* Ricerca articoli */}
        <div className="relative">
          <Input
            placeholder="Cerca articolo per codice o descrizione…"
            value={articoliSearch}
            onChange={(e: any) => setArticoliSearch(e.target.value)}
          />
          {articoli.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded shadow-lg max-h-48 overflow-auto">
              {articoli.map((art: any) => (
                <button
                  key={art.id}
                  onClick={() => aggiungiRigaDaArticolo(art)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
                >
                  <span className="font-medium">{art.sku_interno}</span>
                  <span className="ml-2 text-muted-foreground">{art.descrizione}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {righe.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-2 py-1 text-muted-foreground font-medium">Codice</th>
                <th className="text-left px-2 py-1 text-muted-foreground font-medium">Descrizione</th>
                <th className="text-left px-2 py-1 text-muted-foreground font-medium">U.M.</th>
                <th className="text-right px-2 py-1 text-muted-foreground font-medium">Q.tà</th>
                <th className="text-right px-2 py-1 text-muted-foreground font-medium">Prezzo</th>
                <th className="text-right px-2 py-1 text-muted-foreground font-medium">Importo</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r) => (
                <tr key={r.key} className="border-b border-border">
                  <td className="px-2 py-1">{r.codice_articolo}</td>
                  <td className="px-2 py-1">{r.descrizione}</td>
                  <td className="px-2 py-1">{r.unita_misura}</td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      min="0"
                      step="0.001"
                      value={r.quantita_ordinata}
                      onChange={(e: any) => aggiornaRiga(r.key, 'quantita_ordinata', e.target.value)}
                      className="w-20 text-right"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={r.prezzo_unitario}
                      onChange={(e: any) => aggiornaRiga(r.key, 'prezzo_unitario', e.target.value)}
                      className="w-24 text-right"
                    />
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {currency(Number(r.quantita_ordinata || 0) * Number(r.prezzo_unitario || 0))}
                  </td>
                  <td className="px-2 py-1">
                    <button
                      onClick={() => rimuoviRiga(r.key)}
                      className="text-destructive hover:text-destructive/80 text-xs"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-primary">
                <td colSpan={5} className="px-2 py-2 text-right font-semibold">Totale</td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums">
                  {currency(totale)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </Card>

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Annulla
        </Button>
        <Button
          onClick={handleSalva}
          disabled={!fornitoreId || righe.length === 0 || saving}
        >
          {saving ? 'Salvataggio…' : 'Salva bozza'}
        </Button>
      </div>
    </div>
  );
}
